import {
  useImportStore,
  useManuscriptStore,
} from '@/stores/manuscripts';
import * as manuscriptTypes from '@/lib/constants/manuscripts/store';

// Helper functions for route name checks
function isBuilderRoute(routeName) {
  return routeName === 'manuscript-builder' || routeName === 'manuscript-builder-new';
}

function isLeavingBuilderPage(from, to) {
  return isBuilderRoute(from.name) && !isBuilderRoute(to.name);
}

function isNavigatingToDifferentManuscript(from, to) {
  // Detect navigation between different manuscript instances (e.g., Manuscript A → Manuscript B)
  // This prevents silent data loss when editing one manuscript and navigating to another
  return (
    isBuilderRoute(from.name)
    && isBuilderRoute(to.name)
    && from.params.id !== to.params.id
  );
}

function isSwitchingFromMetadataTab(from, to) {
  // Both routes are builder pages, but tab is changing from metadata to something else
  return (
    isBuilderRoute(from.name)
    && isBuilderRoute(to.name)
    && from.query.tab === 'metadata'
    && to.query.tab !== 'metadata'
  );
}

function isNavigatingBetweenTabs(from, to) {
  // Detect tab changes within the same manuscript (router-link navigation)
  // This captures ?tab=metadata ↔ ?tab=editor changes
  return (
    isBuilderRoute(from.name)
    && isBuilderRoute(to.name)
    && from.params.id === to.params.id
    && from.query.tab !== to.query.tab
  );
}

export function createRouteHandler(router, pinia) {
  const onBeforeEach = (to, from, next) => {
    let navSuccess = true;

    // Check manuscript import guard
    if (from.name === 'manuscript-import' && to.name !== from.name) {
      const manuscriptImportStore = useImportStore(pinia);

      if (manuscriptImportStore.hasImportItems) {
        next(false);

        navSuccess = false;

        manuscriptImportStore[manuscriptTypes.SET_CONFIRM_NAVIGATE_DIALOG]({
          header: 'Leave this page?',
          message: 'You\'ve selected files, but the import hasn\'t started yet. If you leave now, your selected files will be cleared.',
          cancelText: 'Stay on Page',
          confirmText: 'Leave Page',
          onConfirm: () => {
            manuscriptImportStore[manuscriptTypes.CLEAR_IMPORT_DATA]();
            router.push(to);
          },
        });
      }
    }

    // Check manuscript dashboard selection guard
    if (from.name === 'manuscript-dashboard' && to.name !== from.name) {
      const manuscriptStore = useManuscriptStore(pinia);

      if (manuscriptStore.selectedManuscriptIds.length > 0) {
        next(false);

        navSuccess = false;

        manuscriptStore[manuscriptTypes.SET_CONFIRM_NAVIGATE_DIALOG]({
          header: 'Leave this page?',
          message: 'You\'ve selected manuscripts. If you leave now, your selections will be cleared.',
          cancelText: 'Stay on Page',
          confirmText: 'Leave Page',
          onConfirm: () => {
            // CRITICAL: Clear selections BEFORE navigating to prevent guard from triggering again
            manuscriptStore[manuscriptTypes.SET_SELECTED_MANUSCRIPT_IDS]([]);
            router.push(to);
          },
        });
      }
    }

    // Check manuscript builder metadata guard (both new and existing manuscripts)
    // We need to check for unsaved changes in four scenarios:
    // 1. User navigating away from builder entirely (to dashboard, import, etc.)
    // 2. User switching tabs via back button (removing ?tab=metadata query param)
    // 3. User navigating from one manuscript to another manuscript
    // 4. User clicking tab links (router-link) to switch between tabs
    const shouldCheckMetadataChanges = isLeavingBuilderPage(from, to)
      || isSwitchingFromMetadataTab(from, to)
      || isNavigatingToDifferentManuscript(from, to)
      || isNavigatingBetweenTabs(from, to);

    if (shouldCheckMetadataChanges) {
      const manuscriptStore = useManuscriptStore(pinia);

      // Metadata tab is the default for new manuscripts (no query param)
      // Must check unsaved changes when on metadata tab explicitly OR implicitly
      const isOnMetadataTab = from.query.tab === 'metadata' || !from.query.tab;

      if (isOnMetadataTab && manuscriptStore.metadataHasUnsavedChanges) {
        next(false);

        navSuccess = false;

        manuscriptStore[manuscriptTypes.SET_CONFIRM_TAB_NAVIGATION_DIALOG]({
          targetRoute: to,
          onConfirm: () => {
            // CRITICAL: Clear the unsaved changes flag BEFORE navigating
            // to prevent the guard from blocking the navigation again
            manuscriptStore[manuscriptTypes.SET_METADATA_HAS_UNSAVED_CHANGES](false);
            router.push(to);
          },
        });
      }
    }

    return navSuccess;
  };

  return {
    onBeforeEach,
  };
}

export default {
  createRouteHandler,
};
